// The Coordinator's *Create/post req* (G §16 "Create/post req: … Coordinator Create/Edit"; "Coordinator creates and
// posts immediately after HRBP approval"). The card shows where the requisition stands on his creation chain
// ("Request Approved -> Req Creation Pending -> Created -> Posted.") on the requisition until it is posted; *Post*
// (`requisition.post`) opens from the requisition's header. *Create* on an approved request (`requisition.create`) is
// the Hiring Request's own card (HiringRequest.tsx), the one create path. Post never waits for Compensation —
// "Posting is not blocked by Compensation." — so the dialog shows the Search Readiness rows as facts while Post stays
// enabled (DS Q-9, DC-68). Those rows are `readiness.open`'s posting strategy and compensation review prerequisites,
// the same read the rail's Search Readiness card draws, so the dialog and the rail never disagree; a viewer who may not
// call it sees no rows, never a guess.

import { useState } from "react";
import { readinessOpen, requisitionPost, type ReadinessOpenOutput, type RequisitionFull } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import {
  Button, Card, CheckTile, DIALOG_LABELS, Dialog, Field, G1_LABELS, G6_CAPABILITY_LABELS, Gate, Input, METRIC_LABELS,
  MILESTONE_LABELS, Paragraph, Pill, REQUISITION_STATUS, Recorded, Stack, formatDate,
} from "@/mc";
import { requisitionReads, useSettledRead } from "./data";
import { REQ_LABELS, REQ_RULES } from "./labels";
import { prerequisitesOf } from "./SpineCards";

type Channel = "internal" | "external";

/** The Search Readiness prerequisites the Post dialog shows as facts, never a gate on Post. */
const POST_PREREQUISITES: ReadonlySet<string> = new Set(["posting_strategy_set", "compensation_review"]);

/** Those rows as facts: the rail's words and status, without its deep links or hotspots (the dialog leaves nowhere). */
const postFacts = (out: ReadinessOpenOutput) =>
  prerequisitesOf(out.prerequisites.filter((p) => POST_PREREQUISITES.has(p.key))).map((row) => ({ status: row.status, text: row.text }));

/** The card on the requisition (Created → Posted). */
export function CreatePostCard({ r, onPost, timeZone }: { r: RequisitionFull; onPost?: (() => void) | undefined; timeZone: string }) {
  const word = r.posted_at ? REQUISITION_STATUS.posted : REQUISITION_STATUS.created;
  return (
    <Card title={G6_CAPABILITY_LABELS.createPostReq} pills={<Pill>{word}</Pill>} >
      <Stack gap={12}>
        <Paragraph sm>{REQ_RULES.coordinatorCreatesPosts}</Paragraph>
        {!r.posted_at && r.can.post && onPost ? <Button tone="primary" onClick={onPost} >{REQ_LABELS.post}</Button> : null}
        {r.posted_at ? <Recorded right={formatDate(r.posted_at, timeZone)}>{REQUISITION_STATUS.posted}</Recorded> : null}
      </Stack>
    </Card>
  );
}

/** Post: the REQ ID and the channels (none for a confidential search). */
export function PostDialog({ r, open, onClose, canReadiness }: { r: RequisitionFull; open: boolean; onClose: () => void; canReadiness: boolean }) {
  const enabled = open && canReadiness;
  const readiness = useSettledRead<ReadinessOpenOutput>(["readiness", r.id], () => readinessOpen({ requisition_id: r.id }), { enabled });
  const post = useWrite((input: { requisition_id: string; req_no: string; posting_channels: Channel[] }, key) => requisitionPost(input, key), requisitionReads(r.id));
  const [reqNo, setReqNo] = useState(r.req_no ?? "");
  const [channels, setChannels] = useState<Channel[]>(["internal", "external"]);
  const toggle = (channel: Channel, on: boolean) => setChannels((prev) => (on ? [...prev.filter((c) => c !== channel), channel] : prev.filter((c) => c !== channel)));
  const submit = () => void post.mutateAsync({ requisition_id: r.id, req_no: reqNo.trim(), posting_channels: r.confidential ? [] : channels }).then(onClose).catch(() => undefined);
  return (
    <Dialog
      title={REQ_LABELS.post}
      open={open}
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{ label: REQ_LABELS.post, onClick: submit, busy: post.isPending, hs: "req.post" }}
      refused={post.error && !post.error.fieldErrors.req_no ? { reason: post.error.remedy || post.error.message } : undefined}
      
    >
      <Stack gap={16}>
        <Field label={REQ_LABELS.reqId} required error={post.error?.fieldErrors.req_no}>
          <Input value={reqNo} onChange={(event) => setReqNo(event.target.value)} mono  />
        </Field>
        {!r.confidential ? (
          <div role="group" aria-label={MILESTONE_LABELS.posting}>
            <Stack gap={8}>
              <CheckTile checked={channels.includes("internal")} onChange={(on) => toggle("internal", on)} >{G1_LABELS.internal}</CheckTile>
              <CheckTile checked={channels.includes("external")} onChange={(on) => toggle("external", on)} >{G1_LABELS.external}</CheckTile>
            </Stack>
          </div>
        ) : null}
        {canReadiness && !readiness.error ? (
          <Gate
            title={METRIC_LABELS.searchReadiness}
            rows={readiness.data ? postFacts(readiness.data) : []}
            loading={enabled && readiness.isPending}
            mode="view"
          />
        ) : null}
        <Paragraph sm>{REQ_RULES.postingNotBlocked}</Paragraph>
      </Stack>
    </Dialog>
  );
}
